/**
 * OnboardingPage — wizard 4-step v7.1 (Sprint 9 S9.4b).
 *
 * Refactor del wizard Sprint 6 Bloque 5 al modelo v7.1:
 *
 * - Step 1: nivel (beginner | amateur). Sprint 6 tenía 4-tier;
 *   v7.1 colapsa a 2 (definición principiante: NUNCA ha salido a
 *   correr como deporte; amateur: el resto).
 * - Step 2: días disponibles (1-7) con cap a MAX_SESSIONS=4 server-side.
 *   Si el usuario selecciona >4, mostramos warning inline ANTES del
 *   submit; el server confirmará el cap en `sessions_capped=true` y
 *   disparamos toast de aviso post-submit.
 * - Step 3: superficie (asfalto | trail | ambos). Vocab brief v7.1
 *   ("ambos" reemplaza "mixto" en input; backend normaliza al persistir).
 * - Step 4: has_watch (boolean) + fc_max opcional. Si has_watch=false,
 *   ocultamos el campo fc_max (irrelevante sin pulsómetro).
 *
 * Decisiones técnicas:
 * - Estado local en useState (igual que Sprint 6, no Context).
 * - El cap MAX_SESSIONS=4 vive en el server (S9.4a). Aquí solo
 *   mostramos preview del cap como UX; nunca recortamos antes de submit
 *   (el server es la fuente de verdad).
 * - level_segment YA NO existe (S9.3 drop). El wizard ya no bifurca
 *   por nivel — todos los atletas pasan por los mismos 4 pasos.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { submitOnboarding } from "@/api/onboarding";
import { t } from "@/i18n/es";
import {
  ApiError,
  type AthleteLevel,
  type SurfaceInput,
} from "@/types/api";

interface Toast {
  id: number;
  message: string;
  kind: "success" | "error";
}

let toastSeq = 0;

const TOTAL_STEPS = 4;
const MAX_SESSIONS = 4; // mirror del backend (server confirma vía `sessions_capped`)
const DAYS_MIN = 1;
const DAYS_MAX = 7;
const HR_MIN = 120;
const HR_MAX = 220;

interface Answers {
  level: AthleteLevel | null;
  days_available: number | null; // 1..7
  surface: SurfaceInput | null;
  has_watch: boolean | null;
  fc_max_value: string; // raw input; se valida + parsea al submit
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [answers, setAnswers] = useState<Answers>({
    level: null,
    days_available: null,
    surface: null,
    has_watch: null,
    fc_max_value: "",
  });
  const [stepError, setStepError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (message: string, kind: "success" | "error" = "success") => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev, { id, message, kind }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((tt) => tt.id !== id));
      }, 5000);
    },
    [],
  );

  function _validateCurrentStep(): string | null {
    if (step === 1 && answers.level === null) return t.onboarding.errorRequired;
    if (step === 2) {
      if (answers.days_available === null) return t.onboarding.errorRequired;
      if (
        answers.days_available < DAYS_MIN
        || answers.days_available > DAYS_MAX
      )
        return t.onboarding.errorDaysOutOfRange;
    }
    if (step === 3 && answers.surface === null) return t.onboarding.errorRequired;
    if (step === 4) {
      if (answers.has_watch === null) return t.onboarding.errorRequired;
      // fc_max es OPCIONAL siempre. Solo validamos si el user lo escribió.
      if (answers.has_watch === true && answers.fc_max_value.trim() !== "") {
        const num = Number(answers.fc_max_value.trim());
        if (!Number.isFinite(num) || !Number.isInteger(num))
          return t.onboarding.errorFcMaxRequired;
        if (num < HR_MIN || num > HR_MAX)
          return t.onboarding.errorFcMaxRange;
      }
    }
    return null;
  }

  function onNext() {
    const err = _validateCurrentStep();
    if (err) {
      setStepError(err);
      return;
    }
    setStepError(null);
    if (step < TOTAL_STEPS) setStep(((step + 1) as 1 | 2 | 3 | 4));
  }

  function onBack() {
    setStepError(null);
    if (step > 1) setStep(((step - 1) as 1 | 2 | 3 | 4));
  }

  async function onFinish() {
    const err = _validateCurrentStep();
    if (err) {
      setStepError(err);
      return;
    }
    setStepError(null);
    setSubmitting(true);
    try {
      const fcMax =
        answers.has_watch === true && answers.fc_max_value.trim() !== ""
          ? Number(answers.fc_max_value.trim())
          : null;
      const response = await submitOnboarding({
        level: answers.level as AthleteLevel,
        days_available: answers.days_available as number,
        surface: answers.surface as SurfaceInput,
        has_watch: answers.has_watch as boolean,
        fc_max: fcMax,
      });

      // Toast informativo si el server recortó días (cap MAX_SESSIONS=4).
      if (response.sessions_capped) {
        showToast(t.onboarding.capToast, "success");
      } else {
        showToast(t.onboarding.welcomeToast, "success");
      }

      // next_action dirige el redirect post-submit:
      // - "create_plan" → /plan/crear (UX: el atleta entra directo a crear plan).
      // - "redirect_home" → /home (caso edge, p.ej. re-onboarding sin plan nuevo).
      const target =
        response.next_action === "create_plan" ? "/plan/crear" : "/";

      // Pequeña espera para que el toast sea visible antes de redirigir.
      setTimeout(() => navigate(target, { replace: true }), 700);
    } catch (e) {
      if (e instanceof ApiError) {
        switch (e.code) {
          case "level_invalid":
            setStepError(t.onboarding.errorLevelInvalid);
            setStep(1);
            break;
          case "days_available_invalid":
            setStepError(t.onboarding.errorDaysAvailableInvalid);
            setStep(2);
            break;
          case "surface_invalid":
            setStepError(t.onboarding.errorSurfaceInvalid);
            setStep(3);
            break;
          case "has_watch_invalid":
            setStepError(t.onboarding.errorHasWatchInvalid);
            setStep(4);
            break;
          case "fc_max_out_of_range":
          case "fc_max_invalid":
            setStepError(t.onboarding.errorFcMaxOutOfRange);
            setStep(4);
            break;
          default:
            showToast(t.onboarding.errorGeneric, "error");
        }
      } else {
        showToast(t.onboarding.errorGeneric, "error");
      }
      setSubmitting(false);
    }
  }

  return (
    <main
      data-testid="onboarding-page"
      className="mx-auto flex min-h-app max-w-md flex-col gap-6 overflow-x-hidden bg-graphite p-6 text-bone"
    >
      <header>
        <p
          data-testid="onboarding-progress"
          className="text-xs uppercase tracking-widest text-gray-mid"
        >
          {t.onboarding.progressTemplate
            .replace("{n}", String(step))
            .replace("{total}", String(TOTAL_STEPS))}
        </p>
        <h1 className="mt-3 text-2xl font-medium tracking-tight">
          {t.onboarding.title}
        </h1>
      </header>

      {step === 1 && <Step1 answers={answers} setAnswers={setAnswers} />}
      {step === 2 && <Step2 answers={answers} setAnswers={setAnswers} />}
      {step === 3 && <Step3 answers={answers} setAnswers={setAnswers} />}
      {step === 4 && <Step4 answers={answers} setAnswers={setAnswers} />}

      {stepError && (
        <p
          data-testid="onboarding-error"
          role="alert"
          className="text-sm text-danger"
        >
          {stepError}
        </p>
      )}

      <nav className="mt-auto flex items-center justify-between gap-3">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={onBack}
          disabled={step === 1 || submitting}
          className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted transition-colors duration-fast hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-30"
        >
          {t.onboarding.back}
        </button>
        {step < TOTAL_STEPS ? (
          <button
            type="button"
            data-testid="onboarding-next"
            onClick={onNext}
            disabled={submitting}
            className="rounded-md bg-lime px-5 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.onboarding.next}
          </button>
        ) : (
          <button
            type="button"
            data-testid="onboarding-finish"
            onClick={() => void onFinish()}
            disabled={submitting}
            className="rounded-md bg-lime px-5 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.onboarding.finish}
          </button>
        )}
      </nav>

      {/* Toast stack */}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          className="pointer-events-none fixed bottom-4 right-4 z-50 flex max-w-xs flex-col gap-2"
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              data-testid={`toast-${toast.kind}`}
              className="pointer-events-auto rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone"
            >
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------
// Steps individuales — extraídos por claridad
// ---------------------------------------------------------------------

interface StepProps {
  answers: Answers;
  setAnswers: React.Dispatch<React.SetStateAction<Answers>>;
}

function Step1({ answers, setAnswers }: StepProps) {
  const opts: Array<{ value: AthleteLevel; label: string; desc: string }> = [
    {
      value: "beginner",
      label: t.onboarding.step1OptionBeginner,
      desc: t.onboarding.step1OptionBeginnerDesc,
    },
    {
      value: "amateur",
      label: t.onboarding.step1OptionAmateur,
      desc: t.onboarding.step1OptionAmateurDesc,
    },
  ];
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-medium text-bone">{t.onboarding.step1Question}</h2>
      <p className="text-sm text-bone-muted">{t.onboarding.step1Help}</p>
      <fieldset className="flex flex-col gap-2">
        {opts.map((opt) => (
          <label
            key={opt.value}
            className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 transition-colors duration-fast ${
              answers.level === opt.value
                ? "border-lime bg-lime/5 text-bone"
                : "border-border-subtle bg-carbon-raised text-bone-muted hover:border-border-strong"
            }`}
          >
            <span className="flex items-center gap-3">
              <input
                type="radio"
                name="level"
                data-testid={`onboarding-step1-option-${opt.value}`}
                checked={answers.level === opt.value}
                onChange={() =>
                  setAnswers((a) => ({ ...a, level: opt.value }))
                }
                className="accent-lime"
              />
              <span className="text-sm font-medium">{opt.label}</span>
            </span>
            <span className="ml-7 text-xs text-bone-muted">{opt.desc}</span>
          </label>
        ))}
      </fieldset>
    </section>
  );
}

function Step2({ answers, setAnswers }: StepProps) {
  // 1..7 buttons como stepper visual. Tamaño compacto.
  const days = Array.from(
    { length: DAYS_MAX - DAYS_MIN + 1 },
    (_, i) => DAYS_MIN + i,
  );
  const willCap =
    answers.days_available !== null && answers.days_available > MAX_SESSIONS;

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-medium text-bone">{t.onboarding.step2Question}</h2>
      <p className="text-sm text-bone-muted">{t.onboarding.step2Subtitle}</p>
      <fieldset className="grid grid-cols-7 gap-2">
        {days.map((n) => {
          const selected = answers.days_available === n;
          return (
            <label
              key={n}
              className={`flex cursor-pointer items-center justify-center rounded-md border py-3 text-sm font-medium transition-colors duration-fast ${
                selected
                  ? "border-lime bg-lime/5 text-bone"
                  : "border-border-subtle bg-carbon-raised text-bone-muted hover:border-border-strong"
              }`}
            >
              <input
                type="radio"
                name="days-available"
                data-testid={`onboarding-step2-option-${n}`}
                checked={selected}
                onChange={() =>
                  setAnswers((a) => ({ ...a, days_available: n }))
                }
                className="sr-only"
              />
              <span aria-label={`${n} días`}>{n}</span>
            </label>
          );
        })}
      </fieldset>
      {willCap && (
        <p
          data-testid="onboarding-step2-cap-warning"
          role="status"
          className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning"
        >
          {t.onboarding.step2CapWarning}
        </p>
      )}
    </section>
  );
}

function Step3({ answers, setAnswers }: StepProps) {
  const opts: Array<{ value: SurfaceInput; label: string; desc: string }> = [
    {
      value: "asfalto",
      label: t.onboarding.step3OptionAsfalto,
      desc: t.onboarding.step3OptionAsfaltoDesc,
    },
    {
      value: "trail",
      label: t.onboarding.step3OptionTrail,
      desc: t.onboarding.step3OptionTrailDesc,
    },
    {
      value: "ambos",
      label: t.onboarding.step3OptionAmbos,
      desc: t.onboarding.step3OptionAmbosDesc,
    },
  ];
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-medium text-bone">{t.onboarding.step3Question}</h2>
      <fieldset className="flex flex-col gap-2">
        {opts.map((opt) => (
          <label
            key={opt.value}
            className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 transition-colors duration-fast ${
              answers.surface === opt.value
                ? "border-lime bg-lime/5 text-bone"
                : "border-border-subtle bg-carbon-raised text-bone-muted hover:border-border-strong"
            }`}
          >
            <span className="flex items-center gap-3">
              <input
                type="radio"
                name="surface"
                data-testid={`onboarding-step3-option-${opt.value}`}
                checked={answers.surface === opt.value}
                onChange={() =>
                  setAnswers((a) => ({ ...a, surface: opt.value }))
                }
                className="accent-lime"
              />
              <span className="text-sm font-medium">{opt.label}</span>
            </span>
            <span className="ml-7 text-xs text-bone-muted">{opt.desc}</span>
          </label>
        ))}
      </fieldset>
    </section>
  );
}

function Step4({ answers, setAnswers }: StepProps) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-medium text-bone">{t.onboarding.step4Question}</h2>
      <fieldset className="flex flex-col gap-2">
        <label
          className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 transition-colors duration-fast ${
            answers.has_watch === true
              ? "border-lime bg-lime/5 text-bone"
              : "border-border-subtle bg-carbon-raised text-bone-muted hover:border-border-strong"
          }`}
        >
          <span className="flex items-center gap-3">
            <input
              type="radio"
              name="has-watch"
              data-testid="onboarding-step4-option-yes"
              checked={answers.has_watch === true}
              onChange={() => setAnswers((a) => ({ ...a, has_watch: true }))}
              className="accent-lime"
            />
            <span className="text-sm font-medium">{t.onboarding.step4OptionYes}</span>
          </span>
          <span className="ml-7 text-xs text-bone-muted">
            {t.onboarding.step4OptionYesDesc}
          </span>
        </label>
        {answers.has_watch === true && (
          <div
            data-testid="onboarding-step4-fc-block"
            className="ml-7 mt-1 flex flex-col gap-1"
          >
            <label
              htmlFor="fc-max-input"
              className="text-xs uppercase tracking-widest text-gray-mid"
            >
              {t.onboarding.step4FcMaxLabel}
            </label>
            <input
              id="fc-max-input"
              data-testid="onboarding-step4-fc-input"
              type="number"
              inputMode="numeric"
              min={HR_MIN}
              max={HR_MAX}
              placeholder={t.onboarding.step4FcMaxPlaceholder}
              value={answers.fc_max_value}
              onChange={(e) =>
                setAnswers((a) => ({ ...a, fc_max_value: e.target.value }))
              }
              className="w-32 rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
            />
            <p className="text-xs text-bone-muted">
              {t.onboarding.step4FcMaxHelper}
            </p>
          </div>
        )}
        <label
          className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 transition-colors duration-fast ${
            answers.has_watch === false
              ? "border-lime bg-lime/5 text-bone"
              : "border-border-subtle bg-carbon-raised text-bone-muted hover:border-border-strong"
          }`}
        >
          <span className="flex items-center gap-3">
            <input
              type="radio"
              name="has-watch"
              data-testid="onboarding-step4-option-no"
              checked={answers.has_watch === false}
              onChange={() =>
                setAnswers((a) => ({
                  ...a,
                  has_watch: false,
                  fc_max_value: "",
                }))
              }
              className="accent-lime"
            />
            <span className="text-sm font-medium">{t.onboarding.step4OptionNo}</span>
          </span>
          <span className="ml-7 text-xs text-bone-muted">
            {t.onboarding.step4OptionNoDesc}
          </span>
        </label>
        {answers.has_watch === false && (
          <p className="ml-7 mt-1 text-xs text-bone-muted">
            {t.onboarding.step4NoteSensationMode}
          </p>
        )}
      </fieldset>
    </section>
  );
}
